import React, { useState } from 'react';
import axios from 'axios';
import { Send, Loader2, CheckCircle2, Phone, Mail, MapPin } from 'lucide-react';
import { BRAND, SERVICES } from '../mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactForm({ compact = false }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Missing details', description: 'Please fill in your name, email and message.' });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: form.service,
        message: form.message,
      });
      setSent(true);
      toast({ title: 'Request sent!', description: 'Our dispatch team will get back to you shortly.' });
      setForm({ name: '', email: '', phone: '', service: '', message: '' });
    } catch (err) {
      const desc = err?.response?.data?.detail || ('Please call us at ' + BRAND.phone);
      toast({ title: 'Something went wrong', description: desc });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`grid ${compact ? '' : 'lg:grid-cols-5'} gap-10`}>
      {!compact && (
        <div className="lg:col-span-2">
          <h3 className="text-2xl font-extrabold text-[#4E4E50] mb-3">Talk to dispatch</h3>
          <p className="text-gray-600 mb-7">Have a delivery or need a quote? Send us a message or reach out directly &mdash; we respond fast.</p>
          <ul className="space-y-5">
            <li className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-lg bg-[#17A2C4]/10 flex items-center justify-center shrink-0"><Phone size={20} className="text-[#17A2C4]" /></div>
              <div><div className="font-bold text-[#4E4E50]">Call us</div><a href={BRAND.phoneHref} className="text-gray-600 hover:text-[#17A2C4]">{BRAND.phone}</a></div>
            </li>
            <li className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-lg bg-[#17A2C4]/10 flex items-center justify-center shrink-0"><Mail size={20} className="text-[#17A2C4]" /></div>
              <div><div className="font-bold text-[#4E4E50]">Email</div><a href={`mailto:${BRAND.email}`} className="text-gray-600 hover:text-[#17A2C4]">{BRAND.email}</a></div>
            </li>
            <li className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-lg bg-[#17A2C4]/10 flex items-center justify-center shrink-0"><MapPin size={20} className="text-[#17A2C4]" /></div>
              <div><div className="font-bold text-[#4E4E50]">Visit</div><span className="text-gray-600">{BRAND.address.line1}, {BRAND.address.city}, {BRAND.address.region} {BRAND.address.postal}</span></div>
            </li>
          </ul>
        </div>
      )}

      <div className={compact ? '' : 'lg:col-span-3'}>
        {sent ? (
          <div className="bg-[#17A2C4]/8 border border-[#17A2C4]/30 rounded-xl p-10 text-center">
            <CheckCircle2 size={52} className="text-[#17A2C4] mx-auto mb-4" />
            <h3 className="text-xl font-extrabold text-[#4E4E50] mb-2">Thanks &mdash; we got it!</h3>
            <p className="text-gray-600 mb-5">Our dispatch team will be in touch shortly. For urgent jobs call {BRAND.phone}.</p>
            <Button onClick={() => setSent(false)} variant="outline" className="border-[#17A2C4] text-[#17A2C4] hover:bg-[#17A2C4] hover:text-white font-semibold">Send another</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 sm:p-8 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label htmlFor="name">Full name *</Label>
                <Input id="name" value={form.name} onChange={set('name')} placeholder="Jane Doe" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email *</Label>
                <Input id="email" type="email" value={form.email} onChange={set('email')} placeholder="jane@company.com" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" value={form.phone} onChange={set('phone')} placeholder="647-000-0000" />
              </div>
              <div className="space-y-1.5">
                <Label>Service needed</Label>
                <Select value={form.service} onValueChange={set('service')}>
                  <SelectTrigger><SelectValue placeholder="Select a service" /></SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => <SelectItem key={s.id} value={s.name}>{s.name} Delivery</SelectItem>)}
                    <SelectItem value="General Inquiry">General Inquiry</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="message">Message *</Label>
              <Textarea id="message" value={form.message} onChange={set('message')} rows={5} placeholder="Tell us about your pickup, destination and timing..." />
            </div>
            <Button type="submit" disabled={sending} className="w-full bg-[#17A2C4] hover:bg-[#128aa8] text-white font-bold py-6 gap-2">
              {sending ? <><Loader2 size={18} className="animate-spin" /> Sending...</> : <><Send size={18} /> Send request</>}
            </Button>
            <p className="text-xs text-center text-gray-400">This message will be sent to our team at {BRAND.email}.</p>
          </form>
        )}
      </div>
    </div>
  );
}
